import React, { useState, useRef, useEffect, useCallback } from "react";
import LoadingSpinner from "./LoadingSpinner.jsx";
import { tvApi } from "../api/tvApi.jsx";
import { encryptPinCode } from "../utils/cryptoUtils.js";

const TVAuthModal = ({ isOpen, onClose, tvId, tvName, onAuthSuccess }) => {
  const [authCode, setAuthCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authSession, setAuthSession] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [showToastError, setShowToastError] = useState(false); // 토스트 에러 표시용
  const inputRef = useRef(null);

  const startAuthSession = useCallback(async () => {
    if (sessionStarted || isLoading || authSession) {
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      setSessionStarted(true);

      const response = await tvApi.startTvAuth(tvId);

      if (!response.success) {
        throw new Error(response.message || "인증 세션 시작에 실패했습니다.");
      }

      const sessionData = response.data;
      setAuthSession(sessionData);
    } catch (error) {
      setError(error.message || "인증 준비에 실패했습니다. 다시 시도해주세요.");
      setSessionStarted(false);
    } finally {
      setIsLoading(false);
    }
  }, [tvId, sessionStarted, isLoading, authSession]);

  useEffect(() => {
    if (isOpen && tvId && !sessionStarted && !authSession) {
      startAuthSession();
    }
  }, [isOpen, tvId, sessionStarted, authSession, startAuthSession]);

  useEffect(() => {
    if (isOpen && authSession && inputRef.current && !isLoading) {
      const timeoutId = setTimeout(() => {
        // setTimeout 콜백 내부에서도 다시 null 체크
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);

      // cleanup 함수로 timeout 정리
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, authSession, isLoading]);

  useEffect(() => {
    if (!isOpen) {
      setAuthCode("");
      setError("");
      setIsLoading(false);
      setAuthSession(null);
      setSessionStarted(false);
      setShowToastError(false);
    }
  }, [isOpen]);

  // 토스트 에러 자동 숨김
  useEffect(() => {
    if (showToastError) {
      const timer = setTimeout(() => {
        setShowToastError(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showToastError]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!authCode.trim() || authCode.length !== 6 || !authSession) {
      setError("올바른 6자리 인증번호를 입력해주세요.");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const encryptedAuthCode = await encryptPinCode(
        authCode.trim(),
        authSession.publicKey
      );

      const response = await tvApi.authenticateTv(
        tvId,
        encryptedAuthCode,
        authSession.keyId
      );

      if (!response.success) {
        throw new Error(response.message || "TV 인증에 실패했습니다.");
      }

      const sessionId = response.data;
      if (onAuthSuccess) {
        onAuthSuccess(sessionId);
      }
    } catch (error) {
      let errorMessage = "TV 인증에 실패했습니다.";
      if (
        error.message.includes("잘못된") ||
        error.message.includes("invalid")
      ) {
        errorMessage = "잘못된 인증번호입니다. 다시 확인해주세요.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      setShowToastError(true); // 토스트 에러 표시
      setAuthCode("");

      setAuthSession(null);
      setSessionStarted(false); // 재시도를 위해 세션 시작 상태도 초기화합니다.
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    // 상태 초기화 후 useEffect 로직에 따라 startAuthSession이 다시 호출됩니다.
    setError("");
    setAuthCode("");
    setAuthSession(null);
    setSessionStarted(false);
    setShowToastError(false);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    // Enter 키를 눌렀을 때만 제출합니다.
    if (e.key === "Enter" && !isLoading && authCode.length === 6) {
      handleSubmit(e);
    }
  };

  // 인증번호를 마스킹된 형태로 표시하는 함수
  const getMaskedAuthCode = () => {
    return "●".repeat(authCode.length) + "○".repeat(6 - authCode.length);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      {/* 토스트 에러 메시지 */}
      {showToastError && (
        <div className="toast-error">
          <div className="toast-content">
            <span className="toast-icon">❌</span>
            <span className="toast-message">인증번호가 틀렸습니다!</span>
            <button
              className="toast-close"
              onClick={() => setShowToastError(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>TV 인증</h3>
        </div>
        <div className="modal-body">
          <div className="auth-info">
            <p>
              <strong>TV:</strong> {tvName || `TV ${tvId}`}
            </p>
            <p>이 TV를 보기 위해서는 인증번호가 필요합니다.</p>
          </div>

          {error && (
            <div className="error-message">
              <div className="error-icon">⚠️</div>
              <div className="error-text">
                <strong>인증 실패</strong>
                <p>{error}</p>
              </div>
            </div>
          )}

          {isLoading && !authSession && (
            <div
              className="loading-section"
              style={{
                textAlign: "center",
                padding: "20px",
                background: "#f8f9fa",
                borderRadius: "8px",
                marginBottom: "15px",
              }}
            >
              <LoadingSpinner size="medium" />
              <p style={{ margin: "10px 0 0 0", color: "#666" }}>
                인증 준비 중...
              </p>
            </div>
          )}

          {/* 인증 실패 시 `authSession`이 null이 되므로 이 UI가 나타납니다. */}
          {!isLoading && !authSession && error && (
            <div
              className="retry-section"
              style={{
                textAlign: "center",
                padding: "20px",
                background: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "8px",
                marginBottom: "15px",
              }}
            >
              <p style={{ margin: "0 0 15px 0", color: "#856404" }}>
                인증에 실패했습니다.
              </p>
              <button className="btn btn-primary" onClick={handleRetry}>
                다시 시도
              </button>
            </div>
          )}

          {/* 인증 세션이 있을 때만 입력 폼을 보여줍니다. */}
          {authSession && (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="authCode"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  인증번호 (6자리)
                </label>

                {/* 마스킹된 인증번호 표시 */}
                <div
                  style={{
                    position: "relative",
                    cursor: "text",
                  }}
                  onClick={() => inputRef.current?.focus()}
                >
                  <div
                    style={{
                      width: "100%",
                      textAlign: "center",
                      fontSize: "18px",
                      letterSpacing: "8px",
                      fontFamily: "monospace",
                      padding: "12px",
                      border: error ? "2px solid #dc3545" : "2px solid #ddd",
                      borderRadius: "8px",
                      outline: "none",
                      transition: "border-color 0.2s",
                      backgroundColor: error ? "#fff5f5" : "white",
                      userSelect: "none",
                      minHeight: "22px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#333",
                      fontWeight: "bold",
                    }}
                  >
                    {getMaskedAuthCode()}
                  </div>
                  <input
                    id="authCode"
                    ref={inputRef}
                    type="text"
                    value={authCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 6) {
                        setAuthCode(value);
                        if (error) {
                          setError("");
                          setShowToastError(false);
                        }
                      }
                    }}
                    onKeyPress={handleKeyPress}
                    placeholder="123456"
                    className={`form-input ${error ? "error" : ""}`}
                    maxLength="6"
                    disabled={isLoading}
                    autoComplete="off"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      cursor: "text",
                      fontSize: "16px",
                    }}
                  />
                </div>

                <small
                  className="form-help"
                  style={{
                    display: "block",
                    marginTop: "6px",
                    fontSize: "12px",
                    color: error ? "#dc3545" : "#666",
                    textAlign: "center",
                  }}
                >
                  {error
                    ? "올바른 인증번호를 다시 입력해주세요"
                    : "TV에 표시된 6자리 인증번호를 입력하세요"}
                </small>
              </div>

              <div
                className="form-actions"
                style={{
                  display: "flex",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || authCode.length !== 6}
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="small" color="white" />
                      <span>인증 중...</span>
                    </>
                  ) : (
                    "인증"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <style jsx>{`
        /* 토스트 에러 스타일 */
        .toast-error {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 10001;
          animation: slideInRight 0.3s ease-out;
        }

        .toast-content {
          background: #dc3545;
          color: white;
          padding: 12px 16px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 250px;
          max-width: 400px;
        }

        .toast-icon {
          font-size: 16px;
          flex-shrink: 0;
        }

        .toast-message {
          flex-grow: 1;
          font-weight: 500;
          font-size: 14px;
        }

        .toast-close {
          background: none;
          border: none;
          color: white;
          font-size: 18px;
          cursor: pointer;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background-color 0.2s;
          flex-shrink: 0;
        }

        .toast-close:hover {
          background: rgba(255, 255, 255, 0.2);
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* 개선된 에러 메시지 스타일 */
        .error-message {
          background: linear-gradient(135deg, #ffe6e6 0%, #ffecec 100%);
          border: 2px solid #ff6b6b;
          border-radius: 10px;
          padding: 16px;
          marginbottom: 20px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
          animation: shake 0.5s ease-in-out;
        }

        .error-icon {
          font-size: 20px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .error-text {
          flex-grow: 1;
        }

        .error-text strong {
          color: #d32f2f;
          font-size: 16px;
          display: block;
          margin-bottom: 4px;
        }

        .error-text p {
          color: #c62828;
          margin: 0;
          font-size: 14px;
          line-height: 1.4;
        }

        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          10%,
          30%,
          50%,
          70%,
          90% {
            transform: translateX(-5px);
          }
          20%,
          40%,
          60%,
          80% {
            transform: translateX(5px);
          }
        }

        /* 기존 스타일들 */
        .btn-primary {
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 8px 16px;
          cursor: pointer;
        }
        .btn-primary:disabled {
          background-color: #ccc;
          cursor: not-allowed;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          backdrop-filter: blur(2px);
        }
        .modal-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          width: 90%;
          max-width: 450px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 20px 10px 20px;
          border-bottom: 1px solid #eee;
        }
        .modal-header h3 {
          margin: 0;
          color: #333;
          font-size: 18px;
        }
        .modal-close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #999;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .modal-close-btn:hover {
          background: #f5f5f5;
          color: #666;
        }
        .modal-close-btn:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
        .modal-body {
          padding: 20px;
        }
        .auth-info {
          margin-bottom: 20px;
          padding: 15px;
          background: #e3f2fd;
          border-radius: 8px;
          border-left: 4px solid #2196f3;
        }
        .auth-info p {
          margin: 5px 0;
          color: #1565c0;
          font-size: 14px;
        }
        .form-actions button.btn-secondary {
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 10px 20px;
          cursor: pointer;
        }
        .form-actions button.btn-secondary:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        .form-actions button.btn-primary {
          background: #007bff;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 10px 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .form-actions button.btn-primary:disabled {
          background: #ccc;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default TVAuthModal;
